/**
 * Cart Upsell Engine — Metafield-based
 * Reads `custom.upsells` (list of products) from each cart item's product.
 * Place in: assets/upsell-engine.js
 */

window.UpsellEngine = (function () {

  const config = {
    maxUpsells: (window.UpsellSettings && window.UpsellSettings.maxUpsells) || 4,
    cacheTTL:   120000,  // 2 minutes
  };

  // ─── Cache ────────────────────────────────────────────────────────────────
  const _cache = {};
  function cacheGet(k) {
    const e = _cache[k];
    if (!e) return null;
    if (Date.now() - e.ts > config.cacheTTL) { delete _cache[k]; return null; }
    return e.data;
  }
  function cacheSet(k, v) { _cache[k] = { ts: Date.now(), data: v }; }

  // PERF: In-flight deduplication map. If a fetch for the same key is already
  // in progress, subsequent callers await the same Promise instead of firing a
  // duplicate network request. Eliminates parallel duplicate fetches during
  // cold-cache loads when multiple seeds share the same product handle.
  const _inFlight = {};
  function dedupedFetch(key, fetchFn) {
    if (_inFlight[key]) return _inFlight[key];
    const p = fetchFn().finally(() => { delete _inFlight[key]; });
    _inFlight[key] = p;
    return p;
  }

  // ─── Fetch product JSON by handle ─────────────────────────────────────────
  function fetchProduct(handle) {
    const key = 'product:' + handle;
    const cached = cacheGet(key);
    if (cached) return Promise.resolve(cached);
    return dedupedFetch(key, async () => {
      try {
        const r = await fetch(`/products/${handle}.js`);
        if (!r.ok) return null;
        const data = await r.json();
        cacheSet(key, data);
        return data;
      } catch { return null; }
    });
  }

  // ─── Get upsell handles for a single product ──────────────────────────────
  function getUpsellHandlesForProduct(productHandle) {
    const key = 'upsells:' + productHandle;
    const cached = cacheGet(key);
    if (cached !== null) return Promise.resolve(cached);
    return dedupedFetch(key, async () => {
      try {
        const r = await fetch(`/products/${productHandle}?view=upsell-data`);
        if (!r.ok) { cacheSet(key, []); return []; }
        const text = await r.text();
        const m = text.match(/<!--UPSELL_START-->([\s\S]*?)<!--UPSELL_END-->/);
        if (!m) { cacheSet(key, []); return []; }
        const handles = JSON.parse(m[1].trim());
        const cleaned = Array.isArray(handles) ? handles.filter(Boolean) : [];
        cacheSet(key, cleaned);
        return cleaned;
      } catch {
        cacheSet(key, []);
        return [];
      }
    });
  }

  // ─── Main: get upsell products for given seed cart items ──────────────────
  async function getUpsells(cartItems, seedItems) {
    if (!cartItems || cartItems.length === 0) return [];
    const seeds = (seedItems && seedItems.length) ? seedItems : cartItems;

    const handlesPerSeed = await Promise.all(
      seeds.map(item => getUpsellHandlesForProduct(item.handle))
    );

    const seenHandles = new Set();
    const orderedHandles = [];
    for (const list of handlesPerSeed) {
      for (const h of list) {
        if (!seenHandles.has(h)) { seenHandles.add(h); orderedHandles.push(h); }
      }
    }

    if (orderedHandles.length === 0) return [];

    const cartHandles = new Set(cartItems.map(i => i.handle));
    // PERF: Only fetch up to maxUpsells candidates after filtering — no point
    // fetching 20 products when we'll only show 4.
    const toFetch = orderedHandles.filter(h => !cartHandles.has(h)).slice(0, config.maxUpsells * 2);

    if (toFetch.length === 0) return [];

    const products = await Promise.all(toFetch.map(h => fetchProduct(h)));
    return products.filter(p => p && p.available !== false).slice(0, config.maxUpsells);
  }

  // ─── Add to cart ──────────────────────────────────────────────────────────
  function getCartSectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-drawer, cart-items, #main-cart-footer, [id*="cart-icon-bubble"], cart-icon-bubble').forEach(el => {
      const wrap = el.closest('[id^="shopify-section-"]');
      if (wrap) ids.add(wrap.id.replace('shopify-section-', ''));
    });
    return [...ids];
  }

  async function addToCart(variantId, quantity = 1, opts = {}) {
    window._upsellAddInFlight = true;
    try {
      const body = { items: [{ id: variantId, quantity }] };

      const sectionIds = getCartSectionIds();
      if (sectionIds.length) {
        body.sections = sectionIds.join(',');
        body.sections_url = window.location.pathname;
      }

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Add to cart failed');
      const result = await res.json();

      if (result.sections && Object.keys(result.sections).length) {
        applySections(result.sections);
      }
      await refreshDawnCart();
      return result;
    } finally {
      setTimeout(() => { window._upsellAddInFlight = false; }, 1200);
    }
  }

  async function refreshDawnCart() {
    try {
      const res = await fetch('/?sections=cart-drawer');
      let swapped = false;
      if (res.ok) {
        const data = await res.json();
        if (data['cart-drawer']) {
          const doc = new DOMParser().parseFromString(data['cart-drawer'], 'text/html');
          ['CartDrawer-CartItems', 'CartDrawer-Form'].forEach(id => {
            const live = document.getElementById(id);
            const inc  = doc.getElementById(id);
            if (live && inc) { live.innerHTML = inc.innerHTML; swapped = true; }
          });
          const liveFooter = document.querySelector('.drawer__footer');
          const incFooter  = doc.querySelector('.drawer__footer');
          if (liveFooter && incFooter) { liveFooter.innerHTML = incFooter.innerHTML; swapped = true; }
          const drawer = document.querySelector('cart-drawer');
          if (drawer) drawer.classList.remove('is-empty');
        }
      }

      if (!swapped) {
        const cart = await fetch('/cart.js').then(r => r.json());
        const totalEl = document.querySelector('.drawer .totals__total-value, .cart-drawer__footer .totals__total-value');
        if (totalEl) totalEl.textContent = formatMoney(cart.total_price);
      }
    } catch (e) {
      console.warn('[Upsell] refreshDawnCart', e);
    }
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  function applySections(sectionsObj) {
    if (!sectionsObj) return;
    for (const sectionId of Object.keys(sectionsObj)) {
      const html = sectionsObj[sectionId];
      if (!html) continue;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const wrapId = 'shopify-section-' + sectionId;
      const liveWrap = document.getElementById(wrapId);
      const newWrap  = doc.getElementById(wrapId);
      if (liveWrap && newWrap) {
        liveWrap.innerHTML = newWrap.innerHTML;
        continue;
      }
      const targets = {
        'cart-drawer':       ['CartDrawer-CartItems', 'CartDrawer-Form'],
        'cart-icon-bubble':  ['cart-icon-bubble'],
      }[sectionId] || [];
      for (const id of targets) {
        const live = document.getElementById(id);
        const inc  = doc.getElementById(id);
        if (live && inc) live.innerHTML = inc.innerHTML;
      }
    }
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  // ─── Money formatter ──────────────────────────────────────────────────────
  function formatMoney(cents) {
    const fmt = (window.Shopify && window.Shopify.money_format) || '${{amount}}';
    const value = (cents / 100).toFixed(2);
    return fmt.replace('{{amount}}', value)
              .replace('{{amount_no_decimals}}', Math.round(cents / 100))
              .replace('{{amount_with_comma_separator}}', value.replace('.', ','));
  }

  // ─── Global add-to-cart interceptor ───────────────────────────────────────
  function installInterceptor() {
    if (window._upsellInterceptorInstalled) return;
    window._upsellInterceptorInstalled = true;

    const origFetch = window.fetch;
    window.fetch = function (...args) {
      const url = typeof args[0] === 'string' ? args[0] : (args[0]?.url || '');
      const p = origFetch.apply(this, args);
      if (url.includes('/cart/add') || url.includes('/cart/change') || url.includes('/cart/update') || url.includes('/cart/clear')) {
        const fromUpsell = window._upsellAddInFlight === true;
        p.then(() => {
          setTimeout(() => {
            document.dispatchEvent(new CustomEvent('upsell:cart-add', { detail: { fromUpsell }, bubbles: true }));
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          }, 200);
        }).catch(() => {});
      }
      return p;
    };

    const origOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (m, u) { this._url = u; return origOpen.apply(this, arguments); };
    const origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function () {
      if ((this._url || '').includes('/cart/add') || (this._url || '').includes('/cart/change') || (this._url || '').includes('/cart/update') || (this._url || '').includes('/cart/clear')) {
        const fromUpsell = window._upsellAddInFlight === true;
        this.addEventListener('load', () => {
          setTimeout(() => {
            document.dispatchEvent(new CustomEvent('upsell:cart-add', { detail: { fromUpsell }, bubbles: true }));
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          }, 200);
        });
      }
      return origSend.apply(this, arguments);
    };
  }

  installInterceptor();

  // ─── Upsell blocklist (cart-attribute backed) ─────────────────────────────
  let _blocklistMem = null;

  // PERF: _cartPromise caches the in-flight /cart.js fetch so _load() and
  // loadBlocklistFromCart() share one request rather than firing two.
  // Cleared immediately after both consumers have read it.
  let _cartPromise = null;

  function fetchCart() {
    if (!_cartPromise) {
      _cartPromise = fetch('/cart.js?t=' + Date.now(), { cache: 'no-store' })
        .then(r => r.json())
        .finally(() => { _cartPromise = null; });
    }
    return _cartPromise;
  }

  async function loadBlocklistFromCart(cartData) {
    // Accept pre-fetched cart data to avoid a redundant /cart.js round-trip.
    try {
      const cart = cartData || await fetchCart();
      const attr = cart.attributes && cart.attributes._upsell_added;
      const ids = attr ? String(attr).split(',').map(Number).filter(Boolean) : [];
      _blocklistMem = new Set(ids);
    } catch {
      _blocklistMem = _blocklistMem || new Set();
    }
    return _blocklistMem;
  }

  function getBlocklist() {
    return _blocklistMem || new Set();
  }

  async function persistBlocklist() {
    const ids = [...(_blocklistMem || new Set())];
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { _upsell_added: ids.join(',') } }),
      });
    } catch {}
  }

  function blocklistAdd(id) {
    _blocklistMem = _blocklistMem || new Set();
    _blocklistMem.add(Number(id));
    return persistBlocklist();
  }
  function blocklistRemove(id) {
    if (_blocklistMem) { _blocklistMem.delete(Number(id)); return persistBlocklist(); }
    return Promise.resolve();
  }

  // ─── Shared render helper ─────────────────────────────────────────────────
  function buildCard(product, cls, label, onAdd, onCardClick) {
    const variant = product.variants[0];

    const card = document.createElement('div');
    card.className = cls;
    card.setAttribute('role', 'listitem');
    card.dataset.productId = product.id;

    const imgWrap = document.createElement('div');
    imgWrap.className = cls + '__img-wrap';
    const img = document.createElement('img');
    img.className = cls + '__img';
    img.loading = 'lazy';
    img.src = product.featured_image ? product.featured_image + '&width=200' : (product.images?.[0]?.src || product.images?.[0] || '');
    img.alt = product.title;
    imgWrap.appendChild(img);

    const body = document.createElement('div');
    body.className = cls + '__body';
    const name = document.createElement('span');
    name.className = cls + '__name';
    name.textContent = product.title;
    const price = document.createElement('span');
    price.className = cls + '__price';
    price.textContent = formatMoney(variant.price);
    const lbl = document.createElement('span');
    lbl.className = cls + '__label';
    lbl.textContent = label;
    body.appendChild(name); body.appendChild(price); body.appendChild(lbl);

    const btn = document.createElement('button');
    btn.className = cls + '__btn';
    btn.type = 'button';
    btn.dataset.variantId = variant.id;
    btn.textContent = 'Add';

    card.appendChild(imgWrap);
    card.appendChild(body);
    card.appendChild(btn);

    card.addEventListener('click', (e) => {
      if (e.target.closest('.' + cls + '__btn')) return;
      onCardClick(product);
    });
    btn.addEventListener('click', (e) => { e.stopPropagation(); onAdd(product, variant, btn); });

    return card;
  }

  // ─── Generic controller factory ───────────────────────────────────────────
  function makeController(surface) {
    const isDrawer = surface === 'drawer';
    const LIST_ID = isDrawer ? 'upsells-drawer-list' : 'upsells-page-list';
    const ROOT_ID = isDrawer ? 'cart-upsells-drawer' : 'cart-upsells-page';
    const CLS     = isDrawer ? 'upsell-card' : 'upsell-page-card';

    let loading = false;
    let pending = false;
    let debounceTimer = null;
    let loadingTimeout = null;

    function setLoading(val) {
      loading = val;
      clearTimeout(loadingTimeout);
      if (val) loadingTimeout = setTimeout(() => { loading = false; }, 8000);
    }

    // PERF: Debounce bumped to 80ms — collapses the typical burst of 3-4
    // simultaneous events (cart:refresh + upsell:cart-add + MutationObserver)
    // that fire together after any cart change, without perceptible extra delay.
    function load(opts) {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => _load(opts), 80);
    }

    const listEl = () => document.getElementById(LIST_ID);
    const label  = () => {
      const root = document.getElementById(ROOT_ID);
      return (root && root.dataset.label) || 'Recommended for you';
    };

    function showEmpty() {
      const l = listEl();
      if (l && !l.querySelector('.' + CLS)) {
        l.innerHTML = '<p class="upsells__empty">No recommendations available.</p>';
      }
    }

    async function _load(opts) {
      opts = opts || {};
      if (loading) { pending = true; return; }
      setLoading(true);

      const l = listEl();
      if (!l) { setLoading(false); return; }

      try {
        // PERF: Single /cart.js fetch shared with loadBlocklistFromCart() via
        // fetchCart(). Previously two sequential fetches fired: one here and
        // one inside loadBlocklistFromCart(). Now both read from the same
        // Promise — one network round-trip instead of two.
        const cart = await fetchCart();

        if (!cart.items || cart.items.length === 0) {
          l.innerHTML = '';
          setLoading(false);
          return;
        }

        // Pass the already-fetched cart into loadBlocklistFromCart so it
        // doesn't need to fetch /cart.js again.
        await loadBlocklistFromCart(cart);
        const blocklist = getBlocklist();

        // Prune: drop blocked IDs no longer in the cart.
        const cartIds = new Set(cart.items.map(i => Number(i.product_id)));
        let changed = false;
        for (const id of [...blocklist]) {
          if (!cartIds.has(Number(id))) { blocklist.delete(id); changed = true; }
        }
        if (changed) persistBlocklist();

        const seedItems = cart.items.filter(i => !blocklist.has(Number(i.product_id)));
        if (seedItems.length === 0) {
          l.innerHTML = '';
          setLoading(false);
          return;
        }

        // PERF: Kick off upsell-handle fetches immediately, in parallel with
        // nothing blocking them — they were already parallel within getUpsells,
        // but we make it explicit here so readers can see the concurrency model.
        const products = await getUpsells(cart.items, seedItems);

        if (!products || products.length === 0) {
          showEmpty();
          if (!opts._verified) {
            setLoading(false);
            setTimeout(() => _load({ _verified: true }), 500);
            return;
          }
        } else {
          render(products, l);
        }
      } catch (e) {
        console.warn('[Upsell:' + surface + ']', e);
        showEmpty();
      }
      setLoading(false);
      if (pending) { pending = false; _load(); }
    }

    function render(products, l) {
      const existing = [...l.querySelectorAll('.' + CLS)].map(c => c.dataset.productId).join(',');
      const incoming = products.map(p => p.id).join(',');
      if (existing === incoming && existing.length > 0) return;

      // PERF: Build all cards into a DocumentFragment and do a single DOM
      // insertion, instead of N separate appendChild calls each triggering
      // layout.
      l.innerHTML = '';
      l.setAttribute('role', 'list');
      const frag = document.createDocumentFragment();

      products.forEach(product => {
        const card = buildCard(product, CLS, label(),
          async (prod, variant, btn) => {
            if (btn.disabled) return;
            btn.disabled = true;
            btn.classList.add(CLS + '__btn--loading');
            btn.textContent = '…';

            await blocklistAdd(prod.id);

            try {
              await addToCart(variant.id, 1, { reloadAfter: false });
              btn.classList.remove(CLS + '__btn--loading');
              btn.classList.add(CLS + '__btn--added');
              btn.textContent = '✓ Added';
              setTimeout(() => load(), 350);
            } catch (err) {
              btn.disabled = false;
              btn.classList.remove(CLS + '__btn--loading');
              btn.textContent = 'Add';
              blocklistRemove(prod.id);
            }
          },
          (prod) => { if (window.UpsellModal) window.UpsellModal.open(prod); }
        );
        frag.appendChild(card);
      });

      l.appendChild(frag);
    }

    return { load };
  }

  // ─── Boot controllers ─────────────────────────────────────────────────────
  const drawerCtrl = makeController('drawer');
  const pageCtrl   = makeController('page');

  function bootDrawer() {
    drawerCtrl.load();

    function hookItems(drawerEl) {
      const itemsEl = drawerEl.querySelector('cart-drawer-items, #CartDrawer-CartItems');
      if (itemsEl && !itemsEl._upsellItemsHooked) {
        itemsEl._upsellItemsHooked = true;
        new MutationObserver(() => {
          hookItems(drawerEl);
          setTimeout(() => drawerCtrl.load(), 200);
        }).observe(itemsEl, { childList: true, subtree: true });
      }
    }

    function hook() {
      const drawerEl = document.querySelector('cart-drawer');
      if (!drawerEl) return;
      hookItems(drawerEl);

      if (drawerEl._upsellHooked) return;
      drawerEl._upsellHooked = true;

      new MutationObserver(() => {
        hookItems(drawerEl);
        if (drawerEl.hasAttribute('open') || !drawerEl.classList.contains('is-empty')) {
          drawerCtrl.load();
        }
      }).observe(drawerEl, { attributes: true, attributeFilter: ['open', 'class'] });
    }
    hook();
    setInterval(hook, 1500);

    document.addEventListener('cart-drawer:open', () => drawerCtrl.load());
    document.addEventListener('cart:refresh', () => setTimeout(() => drawerCtrl.load(), 150));
    document.addEventListener('cart:updated', () => setTimeout(() => drawerCtrl.load(), 150));
    document.addEventListener('upsell:cart-add', () => setTimeout(() => drawerCtrl.load(), 250));
  }

  function bootPage() {
    if (!document.getElementById('cart-upsells-page')) return;
    pageCtrl.load();

    const cartItems = document.querySelector('cart-items, #main-cart-items');
    if (cartItems) {
      new MutationObserver(() => pageCtrl.load()).observe(cartItems, { childList: true, subtree: true });
    }
    document.addEventListener('cart:refresh', () => pageCtrl.load());
  }

  function boot() { bootDrawer(); bootPage(); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  return {
    getUpsells,
    addToCart,
    formatMoney,
    fetchProduct,
    blocklistAdd,
    blocklistRemove,
    setMaxUpsells(n) { config.maxUpsells = n; },
  };

})();